import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";

import Button from "../CustomButtons/Button";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper";
import "react-image-crop/dist/ReactCrop.css";
import { useController, useWatch } from "react-hook-form";
import NoImageAvailableImage from "../../assets/img/no_image_available.png";
import ReactPlayer from "react-player";
import CropImageDialog from "../CustomImageUpload/CropImageDialog";
import { useDialog } from "../../licensing/utils/DialogProvider";
import { Link } from "@mui/material";

function FormFile(props: {
  name: string;
  imgurl?: string;
  originalUrl?: string;
  srcSet?: string;
  type: "image" | "video" | "file" | "gif";
  nullable?: boolean;
  onDelete?: () => void;
  canCrop?: boolean;
}) {
  const [imagePreviewSrcset, setImagePreviewSrcset] = React.useState<string>();
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | null>(null);
  const [defaultImageUrl, setDefaultImageUrl] = React.useState<string | null>(null);

  const inputField = React.useRef<HTMLInputElement>(null);
  const defaultImageId = React.useRef<string | number | null>(null);
  const filename = useRef<string | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);
  const previewVideoRef = useRef<HTMLDivElement | null>(null);

  const [openDialog] = useDialog();

  const {
    field: { value, onChange },
    fieldState: { isDirty },
  } = useController({ name: props.name });

  const mediaThumb = useWatch({ name: "media_urls." + props.name + "[0].original" });
  const srcSet = useWatch({ name: "media_urls." + props.name + "[0].conversions.responsive.srcset" });

  useEffect(() => {
    if (srcSet) {
      setImagePreviewSrcset(srcSet);
    } else {
      setImagePreviewSrcset(undefined);
    }
  }, [srcSet]);

  useEffect(() => {
    if (mediaThumb) {
      defaultImageId.current = mediaThumb;
    }
  }, [mediaThumb]);

  useEffect(() => {
    if (defaultImageId.current == null) {
      if (typeof value === "number" || typeof value === "string") {
        defaultImageId.current = value;
      }
    }
    if (defaultImageId.current) {
      if (props.imgurl) {
        setDefaultImageUrl(props.imgurl);
      } else if (typeof defaultImageId.current === "string" && defaultImageId.current.includes("://")) {
        setDefaultImageUrl(defaultImageId.current);
      } else if (typeof defaultImageId.current === "string") {
        const uuid = String(defaultImageId.current).replace(/\.[^.]*$/, "");
        setDefaultImageUrl(import.meta.env.VITE_AZURE_STORAGE + "image_uuids/" + uuid + "/" + defaultImageId.current);
      }
    }
  }, [value, mediaThumb, props.imgurl]);

  useEffect(() => {
    function urlToFile(url: string, filename: string) {
      return fetch(url)
        .then(function (res) {
          return res.arrayBuffer();
        })
        .then(function (buf) {
          return new File([buf], filename);
        });
    }

    if (imagePreviewUrl && filename.current) {
      urlToFile(imagePreviewUrl, filename.current).then((file) => {
        onChange(file);
      });
    }
  }, [imagePreviewUrl, onChange]);

  const previewImage = useMemo(() => {
    let showImageUrl = true;
    const url = imagePreviewUrl ?? defaultImageUrl ?? NoImageAvailableImage;
    const matches = url.match(/\.[^.]*$/);
    if (matches?.[0]) {
      switch (matches[0]) {
        case ".png":
        case ".jpeg":
        case ".jpg":
        case ".gif":
          showImageUrl = false;
          break;
        default:
          showImageUrl = true;
          break;
      }
    }

    return (
      <div>
        {(imagePreviewUrl ?? defaultImageUrl) ? (
          <Link href={imagePreviewUrl ?? defaultImageUrl ?? props.imgurl ?? NoImageAvailableImage} target={"_blank"}>
            <img
              alt={"Preview"}
              key={imagePreviewUrl ?? defaultImageUrl}
              onError={() => {
                if (previewImageRef.current) {
                  previewImageRef.current.style.display = "none";
                }
              }}
              ref={previewImageRef}
              sizes={"150px"}
              src={imagePreviewUrl ?? defaultImageUrl ?? NoImageAvailableImage}
              srcSet={imagePreviewUrl ? undefined : (imagePreviewSrcset ?? props.srcSet)}
              style={{ width: "150px", height: "150px", objectFit: "contain" }}
            />
          </Link>
        ) : null}
      </div>
    );
  }, [imagePreviewUrl, defaultImageUrl, props.imgurl, props.srcSet, imagePreviewSrcset]);

  const previewVideo = useMemo(() => {
    return (imagePreviewUrl ?? defaultImageUrl) ? (
      <div key={imagePreviewUrl ?? defaultImageUrl} ref={previewVideoRef} style={{ width: "300px" }}>
        <Link href={props.originalUrl ?? undefined} target={"_blank"}>
          <ReactPlayer
            config={{}}
            controls={false}
            loop={true}
            muted={true}
            onError={() => {
              if (previewVideoRef.current) {
                previewVideoRef.current.style.display = "none";
              }
            }}
            playing={true}
            playsinline={true}
            url={imagePreviewUrl ?? defaultImageUrl ?? ""}
            width={"300"}
          />
        </Link>
      </div>
    ) : null;
  }, [imagePreviewUrl, defaultImageUrl, props.originalUrl]);

  const fileTypeAccept = useMemo(() => {
    if (props.type === "file") {
      return "*/*";
    } else if (props.type === "image") {
      return "image/png,image/jpeg,image/jpg,image/gif,image/webp";
    } else if (props.type === "gif") {
      return "image/gif";
    } else if (props.type === "video") {
      return "video/*";
    }
    return "*/*";
  }, [props.type]);

  return (
    <div>
      <input
        accept={fileTypeAccept}
        onChange={(e) => {
          e.preventDefault();

          const reader = new FileReader();
          if (!e.target.files) {
            console.warn("No files");
            return;
          }
          const file = e.target.files[0];

          reader.addEventListener("load", () => {
            filename.current = file.name;
            setImagePreviewUrl(reader.result as string);

            if (props.type === "image" && props.canCrop) {
              openDialog({
                children: (
                  <CropImageDialog
                    imagePreviewUrl={reader.result as string}
                    setCroppedImage={(croppedImageUrl) => {
                      setImagePreviewUrl(croppedImageUrl);
                    }}
                  />
                ),
              });
            }

            // Allow selecting same file again to change cropping
            if (inputField.current) {
              inputField.current.value = "";
            }
          });
          reader.readAsDataURL(file);
        }}
        ref={inputField}
        style={{ display: "none" }}
        type={"file"}
        {...(props.nullable ? {} : { required: true })}
      />

      {props.type === "image" ? previewImage : null}
      {props.type === "gif" ? previewImage : null}
      {props.type === "video" ? previewVideo : null}
      {props.type === "file" ? (
        <>
          {previewImage}
          {previewVideo}
        </>
      ) : null}

      <div>
        <Button
          color={isDirty && value ? "primary" : undefined}
          onClick={() => {
            if (inputField.current) {
              inputField.current.click();
            }
          }}
        >
          <TranslationWrapper tKey={!isDirty || !value ? "Select a file" : "File selected"} />
        </Button>
        {props.nullable && (imagePreviewUrl || defaultImageUrl) ? (
          <Button
            color={"danger"}
            onClick={() => {
              onChange(null);
              setImagePreviewUrl(null);
              defaultImageId.current = null;
              setDefaultImageUrl(null);
              if (props.onDelete) {
                props.onDelete();
              }
            }}
          >
            <TranslationWrapper tDefault={"Verwijderen"} tKey={"global.CustomImageUpload.deleteImage"} />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

FormFile.defaultProps = {
  nullable: true,
  type: "file",
  canCrop: true,
  imgurl: null,
};

FormFile.propTypes = {
  name: PropTypes.string.isRequired,
  nullable: PropTypes.bool,
  canCrop: PropTypes.bool,
  type: PropTypes.oneOf(["image", "video", "file", "gif"]),
};

export default FormFile;
